import { ReactComponent as DroneSureIcon } from '@assets/components/panel/notification/notification-drone-icon-sure.svg';
import { ReactComponent as DroneUnsureIcon } from '@assets/components/panel/notification/notification-drone-icon-unsure.svg';
import { For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { css } from '@emotion/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { makeSelectPlatformsNamesByCode } from '@redux/config/config.selectors';
import { getPlatformsCodesFromMarks } from '@utils/common.utils';
import { targetColor } from '@utils/map/map.constants';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { ExpiredDroneInfoData, PopupPositionData } from '@/types/popup.types';
import { ReliabilityEnum } from '@/types/sensor/identification.types';

import TargetId from '../TargetId';
import ExpiredDroneDetails from './ExpiredDroneDetails';

export function ExpiredDroneInfo({
  mapConstraintsRef,
  notification,
  top,
  left,
  onClose,
}: Readonly<ExpiredDroneInfoData & PopupPositionData>) {
  const { formatMessage } = useIntl();

  const platformCodes = getPlatformsCodesFromMarks(notification.marks);
  const selectPlatformNames = useMemo(makeSelectPlatformsNamesByCode, []);
  const platforms = useAppSelector((state) => selectPlatformNames(state, platformCodes), shallowEqual);
  const color = targetColor[notification.classification];

  return (
    <DraggablePanel.Root
      key={notification.id}
      constraintsRef={mapConstraintsRef}
      top={top}
      left={left}
      offsetY={-100}
      offsetX={50}
    >
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="targetInfos.targetLostInfo" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '420px', '4k': '488px' }}>
        <VStack gap={0.25} width="100%" height="100%" flex={1} backgroundColor="neutral.900">
          <HStack
            width="100%"
            height="120px"
            gap="standardS"
            paddingX="standardM"
            backgroundColor="neutral.700"
            justifyContent="center"
          >
            <Icon
              as={notification.reliability === ReliabilityEnum.SURE ? DroneSureIcon : DroneUnsureIcon}
              width="70px"
              height="70px"
              css={css({
                '#background': {
                  fill: 'var(--chakra-colors-neutral-black)',
                },
                '#line': {
                  fill: color,
                },
              })}
            />
            <VStack gap={0.5} alignItems="start" width="100%">
              <TargetId
                label={notification.displayId ?? formatMessage({ id: 'target.unknown' })}
                isTechnical={false}
                textStyle="screenStd"
              />
              {/* TODO Drone name */}
              <HStack maxWidth="100%" gap={0.5}>
                <For each={platforms}>
                  {(platform) => (
                    <Text
                      paddingX={0.25}
                      paddingY={0.5}
                      textStyle="screenStd"
                      backgroundColor="neutral.600"
                      textTransform="uppercase"
                      fontWeight="medium"
                      color="neutral.200"
                      key={platform}
                    >
                      {platform}
                    </Text>
                  )}
                </For>
              </HStack>
            </VStack>
          </HStack>
          <ExpiredDroneDetails notification={notification} position={{ x: left, y: top }} />
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
